import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Grid,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { countActivities } from '../../services/company_services';
import PrioritiesModal from './PrioritiesModal';
import eventBus from '../../eventBus';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ProfileItemEmptyState from '../../components/Advisor/Profile/components/ProfileItemEmptyState';
import SurveyLinearStat from '../../components/SurveyLinearStat';
import useSurveyViewer from '../../hooks/useSurveyViewer';

const styles = {
  percentageText: {
    fontWeight: '600',
    fontSize: '18px',
  },
  countText: {
    fontWeight: '400',
    fontSize: '16px',
  },
  miniTileText: {
    fontWeight: '600',
    fontSize: 18,
    lineHeight: '24px',
    color: '#232B35',
  },
  miniTileCount: {
    fontWeight: '600',
    fontSize: '55px',
    color: '#232B35',
  },
  card: { borderRadius: 2, bgcolor: '#fafcff', cursor: 'pointer' },
};

export default function BoardStats() {
  const [surveyResults, loadingResults, questionBody] = useSurveyViewer();
  const [singleQuestion, setSingleQuestion] = useState({ body: questionBody });
  const [yesNoSurveyResults, setYesNoSurveyResults] = useState({
    yesPercentage: 0,
    yesCount: 0,
    noPercentage: 0,
    noCount: 0,
  });
  const [activities, setActivities] = useState({
    activities: 0,
    ideas: 0,
  });
  const [prioritiesOpen, setPrioritiesOpen] = useState(false);
  const { accountDetails } = useAccount();
  const history = useHistory();
  const activeAdvisors = Array.from(
    new Map(
      accountDetails.boardAdvisors
        .filter((advisor) => advisor.advisorStatus === 'active')
        .map((advisor) => [advisor.id, advisor])
    ).values()
  );

  useEffect(async () => {
    let isMounted = true;
    const { data } = await countActivities({
      companyId: accountDetails.id,
    });
    if (isMounted) {
      setActivities(
        data.countActivities || {
          activities: 0,
          ideas: 0,
        }
      );
    }

    eventBus.on('surveyUpdated', (data) => {
      setSingleQuestion({ body: data.question });
      setYesNoSurveyResults({
        yesPercentage: 0,
        yesCount: 0,
        noPercentage: 0,
        noCount: 0,
      });
    });
    return () => {
      isMounted = false;
      eventBus.remove('surveyUpdated');
    };
  }, []);

  useEffect(async () => {
    let isMounted = true;
    if (isMounted && !singleQuestion.body) {
      setSingleQuestion({ body: questionBody });
    }
    return () => {
      isMounted = false;
    };
  }, [questionBody]);
  

  return (
    <Box sx={{ height: '100%' }} id="dashboard-board-stats">
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          py: { xxs: '22px', sm: '32px' },
          px: '20px',
          borderRadius: '12px',
          width: '100%',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xxs: 'column',
              xs: 'row',
            },
            mb: 2,
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontWeight: '600',
              fontSize: '20px',
              lineHeight: '130%',
              color: '#001030',
            }}
          >
            <img
              src="/icons/board_stats.svg"
              alt="Board Stats"
              style={{
                width: '28px',
                height: '28px',
                verticalAlign: 'middle',
                marginRight: '8px',
                marginBottom: '2px',
              }}
            />
            Board Stats
          </Typography>
          <Typography
            id="dashboard-update-priorities"
            sx={{
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '130%',
              color: '#232B35',
              cursor: 'pointer',
              textAlign: 'right',
            }}
            onClick={() => setPrioritiesOpen(true)}
          >
            <Box
              component="img"
              src="/images/dart.png"
              alt="Dart Icon"
              sx={{
                width: '18px',
                height: '18px',
                verticalAlign: 'middle',
                mb: '6px',
                mr: '12px',
              }}
            />
            Update my priorities
          </Typography>
        </Box>
        <Card
          variant="outlined"
          sx={{ borderRadius: 2, bgcolor: '#fafcff', mb: 2 }}
          id="dashboard-survey-results"
        >
          <CardContent>
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '18px',
                lineHeight: '24px',
                color: '#232B35',
                mb: 2,
              }}
            >
              Survey Results
            </Typography>
            {loadingResults ? (
              <LoadingLogoSkeleton />
            ) : singleQuestion.body ? (
              <Typography
                sx={{
                  fontWeight: '400',
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#232B35',
                  whiteSpace: 'pre-line',
                }}
              >
                {singleQuestion.body
                  .split(/(https?:\/\/[^\s]+)/g)
                  .map((part, i) =>
                    part.match(/^https?:\/\/[^\s]+$/) ? (
                      <a
                        key={i}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {part}
                      </a>
                    ) : (
                      part
                    )
                  )}
              </Typography>
            ) : (
              <ProfileItemEmptyState
                items={['100%', '40%'].map((width) => ({
                  width,
                }))}
              />
            )}
            {surveyResults.map((item, index) => (
              <SurveyLinearStat
                key={index}
                optionText={item.body}
                percentage={item.percentage}
                count={item.count}
                loadingResults={loadingResults}
                color={index % 2 === 0 ? '#2B71F6' : '#4FB5F9'}
              />
            ))}
          </CardContent>
        </Card>
        <Grid container spacing={2}>
          <Grid item xxs={12} xs={4}>
            <Card
              variant="outlined"
              sx={styles.card}
              onClick={() => {
                history.push('/advisors');
              }}
              id="dashboard-stat-advisors"
            >
              <CardContent>
                <Typography sx={styles.miniTileCount}>
                  {activeAdvisors.length}
                </Typography>
                <Typography sx={styles.miniTileText}>Advisors</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xxs={12} xs={4}>
            <Card
              variant="outlined"
              sx={styles.card}
              onClick={() => {
                history.push('/board_activity?filter=advisorIdea');
              }}
              id="dashboard-stat-ideas"
            >
              <CardContent>
                <Typography sx={styles.miniTileCount}>
                  {activities.ideas}
                </Typography>
                <Typography sx={styles.miniTileText}>Ideas</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xxs={12} xs={4}>
            <Card
              variant="outlined"
              sx={styles.card}
              onClick={() => {
                history.push('/board_activity');
              }}
              id="dashboard-stat-activity"
            >
              <CardContent>
                <Typography sx={styles.miniTileCount}>
                  {activities.activities}
                </Typography>
                <Typography sx={styles.miniTileText}>Board Activity</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      <PrioritiesModal
        open={prioritiesOpen}
        setIsOpen={setPrioritiesOpen}
        survey={singleQuestion}
      />
    </Box>
  );
}
